import React, { useEffect } from 'react';
import { InlineCustomerSearch } from './CustomerSearch';
import CanCountCell from './CanCountCell';

const WAREHOUSE_OPTIONS = [
  ['Kho 1', 'At Warehouse 1'],
  ['Kho 2', 'At Warehouse 2'],
  ['Kho 3', 'At Warehouse 3'],
  ['ARBOURRIDGE', 'At ARBOURRIDGE'],
  ['Kho 4', 'At Warehouse 4'],
  ['Kho Surrey', 'At Surrey Warehouse'],
  ['Đã chuyển lên Kho 1', 'Transferred to Wh 1'],
];

function Row({ label, children }) {
  return (
    <div style={s.row}>
      <div style={s.rowLabel}>{label}</div>
      <div style={s.rowValue}>{children}</div>
    </div>
  );
}

export default function ShipmentDrawer({
  shipment,
  customers,
  onClose,
  onLocationChange,
  onCustomerChange,
  onEdit,
  onDelete,
  onOpenLabel,
  onOpenInside,
  onMatch,
  onUnassignFlight,
  onCount,
  onUpdateCount,
  countingIds = new Set(),
  mode = 'pending',
}) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!shipment) return null;
  const sh = shipment;
  const transferred = sh.current_location === 'Đã chuyển lên Kho 1';

  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} />
      <aside className="drawer-panel" onClick={e => e.stopPropagation()}>
        <header style={s.header}>
          <div style={{ minWidth: 0 }}>
            <div style={s.eyebrow}>SHIPMENT</div>
            <div style={s.tracking}>{sh.tracking_code}</div>
            {sh.customer_order && (
              <div style={s.cusOrder}>Cust. Order · {sh.customer_order}</div>
            )}
          </div>
          <button onClick={onClose} style={s.closeBtn} title="Close (Esc)">
            <span className="material-symbols-outlined">close</span>
          </button>
        </header>

        <div style={s.body}>
          <Row label="Time">
            <span style={s.muted}>{new Date(sh.scanned_at).toLocaleString('vi-VN')}</span>
          </Row>

          <Row label="Recipient">{sh.receiver_name || '—'}</Row>

          <Row label="Weight">
            <span style={s.mono}>{sh.weight_kg ? `${sh.weight_kg} KG` : '—'}</span>
          </Row>

          {onCount && (
            <Row label="Count">
              <CanCountCell sh={sh} busy={countingIds.has(sh.id)} onCount={onCount} onUpdate={onUpdateCount} />
            </Row>
          )}

          <Row label="Warehouse">
            <span style={s.muted}>{sh.warehouse || '—'}</span>
          </Row>

          {onLocationChange && (
            <Row label="Status">
              <select
                value={sh.current_location || sh.warehouse || ''}
                onChange={e => onLocationChange(sh.id, e.target.value)}
                style={{
                  background: transferred ? 'rgba(22,163,74,0.08)' : 'var(--bg-secondary)',
                  border: `1px solid ${transferred ? 'rgba(22,163,74,0.30)' : 'var(--border-strong)'}`,
                  borderRadius: 6, padding: '6px 10px', fontSize: 12,
                  color: transferred ? 'var(--color-success)' : 'var(--text-secondary)',
                  outline: 'none', cursor: 'pointer', width: '100%',
                }}
              >
                {WAREHOUSE_OPTIONS.map(([v, label]) => (
                  <option key={v} value={v}>{label}</option>
                ))}
              </select>
            </Row>
          )}

          {onCustomerChange && (
            <Row label="Customer">
              <InlineCustomerSearch
                customers={customers}
                currentId={sh.customer_id}
                currentName={sh.customer_id ? `${sh.customer_code} — ${sh.customer_name}` : ''}
                onSelect={v => onCustomerChange(sh, v)}
              />
            </Row>
          )}

          {mode === 'pending' && (
            <Row label="Match">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ color: sh.expected_matched ? 'var(--color-success)' : 'var(--color-warning)', fontWeight: 600, fontSize: 12 }}>
                  {sh.expected_matched ? '✅ Matched' : '⚠ Not matched'}
                </span>
                {onMatch && (
                  <button onClick={() => onMatch(sh)} className="btn btn-secondary btn-sm" style={{ height: 28 }}>
                    <span className="material-symbols-outlined" style={{ fontSize: 14 }}>link</span>
                    Assign code
                  </button>
                )}
              </div>
            </Row>
          )}

          {sh.flight_name && (
            <Row label="Flight">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{
                  fontVariantNumeric: 'tabular-nums', fontWeight: 700, fontSize: 11,
                  color: sh.flight_status === 'flown' ? 'var(--accent)' : 'var(--color-warning)',
                  background: sh.flight_status === 'flown' ? 'rgba(79,140,255,0.10)' : 'rgba(217,119,6,0.10)',
                  border: `1px solid ${sh.flight_status === 'flown' ? 'rgba(79,140,255,0.22)' : 'rgba(217,119,6,0.22)'}`,
                  padding: '3px 8px', borderRadius: 5,
                }}>{sh.flight_name} · {sh.flight_status === 'flown' ? 'Flown' : 'Pending'}</span>
                {onUnassignFlight && (
                  <button onClick={() => onUnassignFlight(sh)} className="btn btn-secondary btn-sm" style={{ height: 28 }}>
                    <span className="material-symbols-outlined" style={{ fontSize: 14, color: 'var(--color-warning)' }}>flight</span>
                    Unassign
                  </button>
                )}
              </div>
            </Row>
          )}

          {/* Images */}
          <div style={s.imgSection}>
            <div style={s.sectionTitle}>IMAGES</div>
            <div style={s.imgGrid}>
              <div style={s.imgCol}>
                <div style={s.imgLabel}>Label</div>
                {sh.image_url ? (
                  <img
                    src={sh.image_url}
                    alt="label"
                    onClick={() => onOpenLabel?.(sh.image_url)}
                    style={s.thumbLabel}
                  />
                ) : (
                  <div style={s.thumbEmpty}>📷</div>
                )}
              </div>
              <div style={s.imgCol}>
                <div style={s.imgLabel}>Inside</div>
                {sh.interior_image_url ? (
                  <img
                    src={sh.interior_image_url}
                    alt="inside"
                    onClick={() => onOpenInside?.(sh.id, sh.tracking_code)}
                    style={s.thumbLabel}
                    title="View inside photos"
                  />
                ) : sh.has_inside_image ? (
                  <button
                    onClick={() => onOpenInside?.(sh.id, sh.tracking_code)}
                    style={s.insideBtn}
                    title="View inside photos"
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: 22, color: 'var(--color-warning)' }}>inventory_2</span>
                    <span style={{ fontSize: 11, fontWeight: 500 }}>Open</span>
                  </button>
                ) : (
                  <div style={s.thumbEmpty}>📷</div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer actions */}
        <footer style={s.footer}>
          {onEdit && (
            <button onClick={() => onEdit(sh)} className="btn btn-secondary" style={{ flex: 1 }}>
              <span className="material-symbols-outlined" style={{ fontSize: 16 }}>edit</span>
              Edit
            </button>
          )}
          {onDelete && (
            <button onClick={() => onDelete(sh.id)} className="btn btn-danger" style={{ flex: 1 }}>
              <span className="material-symbols-outlined" style={{ fontSize: 16 }}>delete</span>
              Delete
            </button>
          )}
        </footer>
      </aside>
    </>
  );
}

const s = {
  header: {
    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
    gap: 12, padding: '18px 20px', borderBottom: '1px solid var(--border)',
  },
  eyebrow: {
    fontSize: 10, fontWeight: 500, letterSpacing: '0.12em',
    color: 'var(--text-muted)', textTransform: 'uppercase',
    marginBottom: 4,
  },
  tracking: {
    fontVariantNumeric: 'tabular-nums', fontWeight: 700,
    fontSize: 18, color: 'var(--text-primary)', wordBreak: 'break-all',
    letterSpacing: '-0.01em',
  },
  cusOrder: {
    fontSize: 12, color: 'var(--text-secondary)', marginTop: 3,
    fontVariantNumeric: 'tabular-nums',
  },
  closeBtn: {
    width: 32, height: 32, border: '1px solid var(--border)',
    background: 'var(--bg-secondary)', color: 'var(--text-secondary)',
    borderRadius: 6, cursor: 'pointer', flexShrink: 0,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  },
  body: { flex: 1, overflowY: 'auto', padding: '4px 20px 16px' },
  row: { padding: '10px 0', borderBottom: '1px solid var(--border)' },
  rowLabel: {
    fontSize: 10, fontWeight: 500, letterSpacing: '0.1em',
    color: 'var(--text-muted)', textTransform: 'uppercase',
    marginBottom: 5,
  },
  rowValue: { fontSize: 13, color: 'var(--text-primary)' },
  muted: { color: 'var(--text-secondary)', fontSize: 12 },
  mono: { fontVariantNumeric: 'tabular-nums', fontWeight: 600 },
  imgSection: { marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)' },
  sectionTitle: {
    fontSize: 10, fontWeight: 500, letterSpacing: '0.1em',
    color: 'var(--text-muted)', textTransform: 'uppercase',
    marginBottom: 10,
  },
  imgGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  imgCol: { display: 'flex', flexDirection: 'column', gap: 6 },
  imgLabel: { fontSize: 11, color: 'var(--text-muted)', fontWeight: 500 },
  thumbLabel: {
    width: '100%', height: 110, objectFit: 'cover',
    borderRadius: 8, border: '1px solid var(--border)',
    cursor: 'zoom-in', display: 'block',
  },
  thumbEmpty: {
    width: '100%', height: 110, display: 'flex',
    alignItems: 'center', justifyContent: 'center',
    background: 'var(--bg-tertiary)', borderRadius: 8,
    fontSize: 24, opacity: 0.4,
  },
  insideBtn: {
    width: '100%', height: 110, display: 'flex',
    flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    gap: 6, background: 'rgba(217,119,6,0.06)',
    border: '1px dashed rgba(217,119,6,0.30)',
    color: 'var(--text-secondary)',
    borderRadius: 8, cursor: 'pointer',
    transition: 'background 110ms, border-color 110ms',
  },
  footer: {
    display: 'flex', gap: 8, padding: '14px 20px',
    borderTop: '1px solid var(--border)', background: 'var(--bg-secondary)',
  },
};
